import { useState, useEffect } from 'react';
import { advanceService, Advance, CreateAdvanceData } from '../../services/advance.service';
import { projectService, Project } from '../../services/project.service';
import { useAuth } from '../../contexts/AuthContext';

const ADVANCE_STATUSES = [
  { value: 'draft', label: '📝 Borrador', badge: 'badge-neutral' },
  { value: 'pending', label: '⏳ Pendiente', badge: 'badge-warning' },
  { value: 'approved', label: '✅ Aprobado', badge: 'badge-success' },
  { value: 'rejected', label: '❌ Rechazado', badge: 'badge-error' },
];

export function AdvancesPage() {
  const { user } = useAuth();
  const [advances, setAdvances] = useState<Advance[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState<Advance | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState<CreateAdvanceData>({
    project_id: '',
    week_number: getCurrentWeekNumber(),
    year: new Date().getFullYear(),
    week_start: getWeekStart(),
    week_end: getWeekEnd(),
    description: '',
    progress_percentage: 0,
    photos: [],
  });

  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  function getCurrentWeekNumber(): number {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = (now.getTime() - start.getTime()) / 86400000;
    return Math.ceil((diff + start.getDay() + 1) / 7);
  }

  function getWeekStart(): number {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday.getTime();
  }

  function getWeekEnd(): number {
    const monday = new Date(getWeekStart());
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return sunday.getTime();
  }

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [advancesData, projectsData] = await Promise.all([
        advanceService.getAdvances(selectedProject === 'all' ? undefined : selectedProject),
        projectService.getProjects(),
      ]);
      setAdvances(advancesData);
      setProjects(projectsData);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      await advanceService.createAdvance(formData);
      await loadData();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Error al guardar avance');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Seguro que quieres eliminar este avance?')) return;
    try {
      await advanceService.deleteAdvance(id);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar avance');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await advanceService.approveAdvance(id);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al aprobar avance');
    }
  };

  const handleRejectSubmit = async () => {
    if (!selectedAdvance) return;
    if (!rejectReason.trim()) {
      setError('Debes proporcionar un motivo de rechazo');
      return;
    }

    try {
      await advanceService.rejectAdvance(selectedAdvance.id, rejectReason);
      await loadData();
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedAdvance(null);
    } catch (err: any) {
      setError(err.message || 'Error al rechazar avance');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      project_id: '',
      week_number: getCurrentWeekNumber(),
      year: new Date().getFullYear(),
      week_start: getWeekStart(),
      week_end: getWeekEnd(),
      description: '',
      progress_percentage: 0,
      photos: [],
    });
    setPhotoPreviews([]);
    setSelectedFiles([]);
    setError('');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files);
    setSelectedFiles(prev => [...prev, ...fileArray]);

    const newPreviews: string[] = [];
    fileArray.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        newPreviews.push(event.target?.result as string);
        if (newPreviews.length === fileArray.length) {
          setPhotoPreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const getStatusInfo = (status: string) => {
    return ADVANCE_STATUSES.find(s => s.value === status) || ADVANCE_STATUSES[0];
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : projectId.slice(0, 8);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredAdvances = advances.filter(advance => {
    const matchesStatus = filterStatus === 'all' || advance.status === filterStatus;
    return matchesStatus;
  });

  const canApprove =
    user?.role === 'sys_admin' ||
    user?.role === 'general_manager' ||
    user?.role === 'project_lead';

  const canCreate =
    canApprove || user?.role === 'contractor';

  const activeFiltersCount =
    (selectedProject !== 'all' ? 1 : 0) + (filterStatus !== 'all' ? 1 : 0);

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="page-header-bar">
        <div>
          <h1>Avances de Obra</h1>
          <p className="subtitle">Registro de avances semanales de obra</p>
        </div>
        <div className="actions">
          <button onClick={loadData} className="btn btn-ghost">
            🔄 Actualizar
          </button>
          {canCreate && (
            <button
              onClick={() => {
                setFormData({
                  project_id: '',
                  week_number: getCurrentWeekNumber(),
                  year: new Date().getFullYear(),
                  week_start: getWeekStart(),
                  week_end: getWeekEnd(),
                  description: '',
                  progress_percentage: 0,
                  photos: [],
                });
                setPhotoPreviews([]);
                setSelectedFiles([]);
                setShowModal(true);
              }}
              className="btn btn-primary"
            >
              + Nuevo Avance
            </button>
          )}
        </div>
      </div>

      {error && !showModal && !showRejectModal && (
        <div className="info-panel error">
          <span className="info-panel-icon">❌</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{error}</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-filters">
          <select
            className="form-select"
            value={selectedProject}
            onChange={(e) => {
              setSelectedProject(e.target.value);
              setTimeout(loadData, 0);
            }}
          >
            <option value="all">Todos los proyectos</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>

          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Todos los estados</option>
            {ADVANCE_STATUSES.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>

          {activeFiltersCount > 0 && (
            <span className="filter-badge">
              {activeFiltersCount} filtro{activeFiltersCount > 1 ? 's' : ''} activo{activeFiltersCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Contenido */}
      {isLoading && advances.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">Cargando avances...</p>
        </div>
      ) : filteredAdvances.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            {advances.length === 0 ? '📈' : '🔍'}
          </div>
          <h3 className="empty-state-title">
            {advances.length === 0 ? 'No hay avances registrados' : 'Sin resultados'}
          </h3>
          <p className="empty-state-text">
            {advances.length === 0
              ? 'Los avances semanales aparecerán aquí conforme se registren.'
              : 'No se encontraron avances con los filtros aplicados.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredAdvances.map((advance) => {
            const statusInfo = getStatusInfo(advance.status);
            return (
              <div key={advance.id} className="card" style={{ padding: '18px' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                      {getProjectName(advance.project_id)}
                    </h3>
                    <p style={{ fontSize: '12px', color: 'var(--gray)', margin: '2px 0 0 0' }}>
                      Semana {advance.week_number} • {advance.year}
                    </p>
                  </div>
                  <span className={`badge ${statusInfo.badge}`}>{statusInfo.label}</span>
                </div>

                <p style={{ fontSize: '12px', color: 'var(--gray)', margin: '0 0 10px 0' }}>
                  📅 {formatDate(advance.week_start)} — {formatDate(advance.week_end)}
                </p>

                {advance.description && (
                  <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px 0', lineHeight: 1.5 }}>
                    {advance.description}
                  </p>
                )}

                {/* Progress */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--gray)', marginBottom: '4px' }}>
                    <span>Avance</span>
                    <span style={{ fontWeight: 700, color: 'var(--primary)' }}>
                      {advance.progress_percentage}%
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${Math.min(advance.progress_percentage, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Fotos */}
                {advance.photos && advance.photos.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {advance.photos.map((photo, idx) => (
                      <div
                        key={photo.id}
                        style={{
                          width: '48px',
                          height: '48px',
                          flexShrink: 0,
                          borderRadius: '8px',
                          overflow: 'hidden',
                          background: 'var(--light-gray)',
                          border: '1px solid var(--light-gray)',
                        }}
                      >
                        <img
                          src={photo.thumbnail_url || photo.url}
                          alt={`Foto ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Rechazo */}
                {advance.rejected_reason && (
                  <div className="info-panel error" style={{ marginBottom: '12px', padding: '10px 14px' }}>
                    <span className="info-panel-icon" style={{ fontSize: '16px' }}>❌</span>
                    <div className="info-panel-body">
                      <p className="info-panel-text" style={{ fontSize: '12px' }}>
                        <strong>Motivo de rechazo:</strong> {advance.rejected_reason}
                      </p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', paddingTop: '12px', borderTop: '1px solid var(--light-gray)', flexWrap: 'wrap' }}>
                  {advance.status === 'pending' && canApprove && (
                    <>
                      <button onClick={() => handleApprove(advance.id)} className="btn-action-approve">
                        ✅ Aprobar
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAdvance(advance);
                          setRejectReason('');
                          setShowRejectModal(true);
                        }}
                        className="btn-action-reject"
                      >
                        ❌ Rechazar
                      </button>
                    </>
                  )}
                  <button className="btn-action-view">👁️ Ver</button>
                  {(advance.status === 'draft' || advance.status === 'pending') && canCreate && (
                    <button onClick={() => handleDelete(advance.id)} className="btn-action-delete">
                      🗑️ Eliminar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal crear */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📝 Nuevo Avance Semanal</h3>
              <button onClick={handleCloseModal} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label required">Proyecto</label>
                    <select
                      className="form-select"
                      required
                      value={formData.project_id}
                      onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    >
                      <option value="">Seleccionar proyecto...</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row cols-2">
                  <div className="form-col">
                    <label className="form-label required">Semana</label>
                    <input
                      type="number"
                      className="form-input"
                      required
                      min={1}
                      max={53}
                      value={formData.week_number}
                      onChange={(e) => setFormData({ ...formData, week_number: parseInt(e.target.value) })}
                    />
                  </div>

                  <div className="form-col">
                    <label className="form-label required">Año</label>
                    <input
                      type="number"
                      className="form-input"
                      required
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label required">Avance (%)</label>
                    <input
                      type="number"
                      className="form-input"
                      required
                      min={0}
                      max={100}
                      value={formData.progress_percentage}
                      onChange={(e) => setFormData({ ...formData, progress_percentage: parseInt(e.target.value) })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label">Descripción</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe el trabajo realizado esta semana..."
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label">Fotos de evidencia</label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      className="form-input"
                      style={{ padding: '8px' }}
                    />
                    {photoPreviews.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                        {photoPreviews.map((preview, index) => (
                          <div
                            key={index}
                            style={{
                              position: 'relative',
                              width: '64px',
                              height: '64px',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '1px solid var(--light-gray)',
                            }}
                          >
                            <img
                              src={preview}
                              alt={`Foto ${index + 1}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(index)}
                              style={{
                                position: 'absolute',
                                top: '2px',
                                right: '2px',
                                background: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="info-panel error" style={{ marginTop: '16px' }}>
                    <span className="info-panel-icon">❌</span>
                    <div className="info-panel-body">
                      <p className="info-panel-text">{error}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-ghost">
                  Cancelar
                </button>
                <button type="submit" disabled={isSaving} className="btn btn-primary">
                  {isSaving ? 'Guardando...' : 'Crear Avance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal rechazo */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>❌ Rechazar Avance</h3>
              <button onClick={() => setShowRejectModal(false)} className="modal-close">✕</button>
            </div>

            <div className="modal-body">
              <div className="form-row">
                <div className="form-col">
                  <label className="form-label required">Motivo de rechazo</label>
                  <textarea
                    className="form-textarea"
                    rows={4}
                    required
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Explica el motivo por el cual se rechaza este avance..."
                  />
                </div>
              </div>

              {error && (
                <div className="info-panel error" style={{ marginTop: '16px' }}>
                  <span className="info-panel-icon">❌</span>
                  <div className="info-panel-body">
                    <p className="info-panel-text">{error}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                className="btn btn-ghost"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleRejectSubmit}
                disabled={isSaving}
                className="btn btn-danger"
              >
                {isSaving ? 'Procesando...' : 'Rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}